// Site models: a monument on the top-ranked wonder hexes and a small prop on deposit hexes, at close
// zoom. Symbols of the record, not of its size: models are picked by category or resource kind,
// never by any quantity.
//
//   WonderSite ─► pickModels (best first, one per 5x5 hexes) ─► footing slab + landmark on it
//   ResourceSite ─► prop on a ring round the hex centre
//   camera ─► zoom bands ─► frustum test ─► visible set ─► one InstancedMesh per slot
//
//        landmark (castle | dome | temple | church)
//        ┌──────┐
//   ~~~~~│footing│~~~~~   footing: a ground-tinted slab from below the lowest to above the highest
//        └──────┘        ground under it (footing.ts), so a hill never buries it
//
// Slots: footing, landmark.castle / .dome / .temple / .church, pumpjack, derrick, offshore and mine
// (headframe + ore heap, the heap tinted per kind). Natural sites get no model: their chip marks them.
// The merge into the meshes reruns only when the visible set changes, so a still camera costs nothing.

import * as THREE from 'three/webgpu';
import { attribute, mix, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { WORLD_W, hexCenter, hexCol, hexRow } from '../../core/grid.ts';
import type { ViewCore } from '../index.ts';
import { wrapDelta } from '../camera.ts';
import { PaintedMaterial, createPaintStyle, type PaintStyle } from '../lighting.ts';
import { screenNode } from '../tsl/color.ts';
import { GROUND, GROUND_COUNT, HEX_KIND, hexHash, type HexShapes } from '../terrain/heights.ts';
import { crownProfile } from '../terrain/mesher.ts';
import { BIOME_COLORS } from '../terrain/palette.ts';
import { footingAt, type Footing } from './footing.ts';
import { compose, footingGeometry, heapGeometry, loadAssets, muted, templeGeometry, withoutPad, type Part } from './geometry.ts';
import { heapColor } from './palette.ts';
import {
  BANDS, bandAlpha, landmarkOf, pickModels, propKindOf, resourcePropAlpha, ringSpots, type Landmark, type PropKind, type ResourceSite,
  type ResourceView, type WonderSite,
} from './plan.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** URL of public/assets: the view's default (src/view/index.ts DEFAULT_ASSETS). */
const ASSET_BASE = '/assets';

/** Asset ids sites draws (manifest.json). */
const ASSET = {
  castle: 'landmark.castle',
  dome: 'landmark.capitol',
  church: 'landmark.church',
  stone: 'rock.tall',
  pumpjack: 'industry.pumpjack',
  derrick: 'industry.derrick',
  offshore: 'industry.offshore',
  mine: 'industry.mine',
} as const;

type SlotId = 'footing' | `landmark.${Landmark}` | PropKind;

/** Whether a resource hex also carries a wonder's monument. */
type Company = 'wonder' | 'alone';

const CAPACITY = 2048;
const ROUGHNESS = 0.85;
const PROP_RIM = 0.04;
const PROP_SPECULAR = 0.04;

// Monument recipe, in model units (instance scale 1). The landmark is tinted gold through its team
// mask, a neutral colour that no owner uses; the footing takes the ground's own colour.
const CASTLE_SCALE = 2.2;
const DOME_SCALE = 1.7;
const TEMPLE_SCALE = 1.7;
const CHURCH_SCALE = 3;
const CHURCH_OFFSET = 0.1;
const STONE_SCALE = 1.7;
const STONE_RING = 0.3;
const STONE_COUNT = 3;
const STONE_PHASE = 1;
const GOLD = '#d9b45a';
const GOLD_GLOW = '#ffcf6b';
const GLOW_STRENGTH = 0.3;
const HEAP_OFFSET = 0.16;
const HEADFRAME_OFFSET = -0.07;
const PROP_COLOUR_KEEP = 0.6; // share of the rigs' own colour that stays (the reviewer found them the loudest thing on the map)

// Footing.
const FOOT_RADIUS = 0.5;
const FOOT_VALUE = 0.9; // the slab is a touch darker than the ground it stands on
const GROUND_SHADES: Readonly<Record<number, keyof typeof BIOME_COLORS>> = {
  [GROUND.GRASS]: 'grass',
  [GROUND.PLAINS]: 'plains',
  [GROUND.DESERT]: 'desert',
  [GROUND.TUNDRA]: 'tundra',
  [GROUND.SNOW]: 'snow',
  [GROUND.BROADLEAF]: 'forest',
  [GROUND.CONIFER]: 'forest',
  [GROUND.JUNGLE]: 'jungle',
};
const BYTE = 255;

// Placement.
const RING_LONE = 0.42;
const RING_SHARED = 0.47;
const RING_PEAK = 0.62;
const RING_WITH_WONDER = 0.72;
const MONUMENT_SEED = 3;
const PROP_SCALE: Readonly<Record<PropKind, number>> = { pumpjack: 2.2, derrick: 1.6, offshore: 1, mine: 2 };
const SINK = 0.02;
const AMP_SINK = 1;
const GROW_FROM = 10;
const GROW_TO = 40;
const GROW_MAX = 1.5;
const MODEL_GROW_SHARE = 0.5; // monuments grow half as much as props: the footing does not grow at all
const SCALE_STEPS = 12; // scale changes are quantised so the merge reruns a few times per zoom, not per frame
const SPHERE_MARGIN = 1.25;

interface Placement {
  key: string;
  sourced: boolean;
  slot: SlotId;
  group: 'wonder' | 'resource';
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Horizontal scale at rest (model units to world units). */
  scale: number;
  /** Vertical scale at rest. */
  depth: number;
  /** Whether the camera height enlarges it (a footing must not: its footprint is what was sampled). */
  grows: boolean;
  /** Bounding sphere radius at rest and its centre's height above the origin, world units. */
  reach: number;
  lift: number;
  tint: THREE.Color | null;
}

interface Slot {
  mesh: THREE.InstancedMesh;
  tint: THREE.InstancedBufferAttribute | null;
  radius: number;
  lift: number;
}

/** Ground height at local offset (lx, lz) of a land hex (features/cities.ts groundAt). */
function groundAt(shapes: HexShapes, t: number, lx: number, lz: number): number {
  const r = Math.min(1, Math.hypot(lx, lz));
  const crown = shapes.crown[t] * shapes.crownScale[t] * crownProfile(r);
  return shapes.base[t] + shapes.lift[t] + crown - SINK - AMP_SINK * shapes.amp[t];
}

/** The ground's own colour over a hex, weighted by its ground cover, a touch darker: the footing's tint. */
function groundTint(shapes: HexShapes, t: number): THREE.Color {
  const out = new THREE.Color(0, 0, 0);
  let total = 0;
  for (let c = 0; c < GROUND_COUNT; c++) {
    const w = shapes.ground[t * GROUND_COUNT + c] / BYTE;
    if (w === 0) {
      continue;
    }

    const [a, b] = BIOME_COLORS[GROUND_SHADES[c]];
    const mid = new THREE.Color(a).lerp(new THREE.Color(b), 0.5);
    out.r += mid.r * w;
    out.g += mid.g * w;
    out.b += mid.b * w;
    total += w;
  }
  if (total === 0) {
    return new THREE.Color(BIOME_COLORS.plains[0]).multiplyScalar(FOOT_VALUE);
  }
  return out.multiplyScalar(FOOT_VALUE / total);
}

/** Growth of props with camera height: a little larger farther out, so they stay legible. */
function growth(height: number): number {
  const t = Math.min(1, Math.max(0, (height - GROW_FROM) / (GROW_TO - GROW_FROM)));
  return 1 + (GROW_MAX - 1) * t * t * (3 - 2 * t);
}

function quantise(v: number): number {
  return Math.round(v * SCALE_STEPS) / SCALE_STEPS;
}

/** Column-major T · R_y(yaw) · S(s, sy, s) into `out` at `at` (features/layer.ts writeMatrix). */
function writeMatrix(out: Float32Array, at: number, x: number, y: number, z: number, yaw: number, s: number, sy = s): void {
  const c = Math.cos(yaw);
  const sn = Math.sin(yaw);
  out.fill(0, at, at + 16);
  out[at] = c * s;
  out[at + 2] = -sn * s;
  out[at + 5] = sy;
  out[at + 8] = sn * s;
  out[at + 10] = c * s;
  out[at + 12] = x;
  out[at + 13] = y;
  out[at + 14] = z;
  out[at + 15] = 1;
}

export class SiteProps {
  readonly group = new THREE.Group();
  private readonly shapes: HexShapes;
  private readonly style: PaintStyle;
  private readonly slots = new Map<SlotId, Slot>();
  private placements: Placement[] = [];
  private readonly frustum = new THREE.Frustum();
  private readonly projView = new THREE.Matrix4();
  private readonly sphere = new THREE.Sphere();
  private signature = -1;
  private unsourced: string[] = [];

  private constructor(shapes: HexShapes) {
    this.shapes = shapes;
    this.style = createPaintStyle();
    this.style.rimStrength.value = PROP_RIM;
    this.style.specular.value = PROP_SPECULAR;
    this.group.name = 'sites';
  }

  /** Load the models; a missing asset leaves its slot empty (logged), the chips still work. */
  static async create(core: ViewCore): Promise<SiteProps> {
    const props = new SiteProps(core.shapes);
    const ids = Object.values(ASSET);
    const assets = await loadAssets(ASSET_BASE, ids);
    props.build(assets);
    return props;
  }

  /**
   * Place the models: a footing and a landmark on each hex pickModels keeps, a prop on each deposit
   * hex. Only hexes the records name; nothing invented. Returns the keys of the wonders that got a
   * model, so their chips can step aside.
   */
  setSites(wonders: readonly WonderSite[], resources: readonly ResourceSite[], cityHexes: ReadonlySet<number>): ReadonlySet<string> {
    const out: Placement[] = [];
    const monumentHexes = new Set<number>();
    const modelled = new Set<string>();
    const feet = new Map<string, Footing>();
    const models = pickModels(wonders, (w) => {
      const foot = this.canStand(w, cityHexes);
      if (foot) {
        feet.set(w.key, foot);
      }
      return foot !== null;
    });
    for (const w of models) {
      const foot = feet.get(w.key);
      if (!foot) {
        continue;
      }

      out.push(...this.monument(w, foot));
      monumentHexes.add(w.hex);
      modelled.add(w.key);
    }

    const byHex = new Map<number, ResourceSite[]>();
    for (const r of resources) {
      const list = byHex.get(r.hex);
      if (list) {
        list.push(r);
        continue;
      }
      byHex.set(r.hex, [r]);
    }
    for (const [hex, sites] of byHex) {
      if (cityHexes.has(hex)) {
        continue;
      }
      out.push(...this.resourceProps(hex, sites, monumentHexes.has(hex) ? 'wonder' : 'alone'));
    }

    this.placements = out;
    this.signature = -1;
    return modelled;
  }

  /** Show one instance of every slot at (x, y, z) so each pipeline compiles now, not at first zoom. */
  warm(x: number, y: number, z: number): void {
    for (const s of this.slots.values()) {
      writeMatrix(s.mesh.instanceMatrix.array as Float32Array, 0, x, y, z, 0, 1);
      s.tint?.setXYZ(0, 1, 1, 1);
      s.mesh.count = Math.max(s.mesh.count, 1);
      s.mesh.visible = true;
      s.mesh.instanceMatrix.needsUpdate = true;
      if (s.tint) {
        s.tint.needsUpdate = true;
      }
    }

    // No real signature equals this, so the next update() rebuilds every slot.
    this.signature = -1;
  }

  /** Keys of the drawn models whose records lack a source id. */
  unsourcedKeys(): readonly string[] {
    return this.unsourced;
  }

  /** Choose and write this frame's instances; merges again only when the visible set changes. */
  update(camera: THREE.Camera, camX: number, height: number, view: ResourceView): void {
    const wonderAlpha = quantise(bandAlpha(height, BANDS.wonderModel));
    const resourceAlpha = quantise(resourcePropAlpha(height, view));
    const grow = quantise(growth(height));
    camera.updateMatrixWorld();
    this.projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projView);

    // Visible placements and a signature of the set (index, wrap copy, scale step).
    const visible: Array<{ p: Placement; x: number; alpha: number }> = [];
    let sig = Math.imul(2166136261 ^ Math.round(grow * SCALE_STEPS), 16777619);
    for (let i = 0; i < this.placements.length; i++) {
      const p = this.placements[i];
      const alpha = p.group === 'wonder' ? wonderAlpha : resourceAlpha;
      if (alpha === 0) {
        continue;
      }

      const x = camX + wrapDelta(camX, p.x);
      const g = this.growOf(p, grow);
      this.sphere.center.set(x, p.y + p.lift * g, p.z);
      this.sphere.radius = p.reach * g * SPHERE_MARGIN;
      if (!this.frustum.intersectsSphere(this.sphere)) {
        continue;
      }

      visible.push({ p, x, alpha });
      const copy = Math.round((x - p.x) / WORLD_W) + 1;
      sig = Math.imul(sig ^ (((i * 3 + copy) * (SCALE_STEPS + 1)) + Math.round(alpha * SCALE_STEPS)), 16777619);
    }

    sig = (sig ^ visible.length) >>> 0;
    if (sig === this.signature) {
      return;
    }
    this.signature = sig;
    this.write(visible, grow);
  }

  dispose(): void {
    for (const s of this.slots.values()) {
      s.mesh.geometry.dispose();
      (s.mesh.material as THREE.Material).dispose();
      s.mesh.dispose();
    }
    this.slots.clear();
    this.group.clear();
  }

  private write(visible: ReadonlyArray<{ p: Placement; x: number; alpha: number }>, grow: number): void {
    const fill = new Map<SlotId, number>();
    const unsourced = new Set<string>();
    for (const { p, x, alpha } of visible) {
      const s = this.slots.get(p.slot);
      const i = fill.get(p.slot) ?? 0;
      if (!s || i >= CAPACITY) {
        continue;
      }

      fill.set(p.slot, i + 1);
      const g = this.growOf(p, grow) * alpha;
      writeMatrix(s.mesh.instanceMatrix.array as Float32Array, i * 16, x, p.y, p.z, p.yaw, p.scale * g, p.depth * g);
      if (s.tint && p.tint) {
        s.tint.setXYZ(i, p.tint.r, p.tint.g, p.tint.b);
      }
      if (!p.sourced) {
        unsourced.add(p.key);
      }
    }
    this.unsourced = [...unsourced];

    for (const [id, s] of this.slots) {
      const n = fill.get(id) ?? 0;
      s.mesh.count = n;
      s.mesh.visible = n > 0;
      if (n === 0) {
        continue;
      }

      s.mesh.instanceMatrix.clearUpdateRanges();
      s.mesh.instanceMatrix.addUpdateRange(0, n * 16);
      s.mesh.instanceMatrix.needsUpdate = true;
      if (s.tint) {
        s.tint.clearUpdateRanges();
        s.tint.addUpdateRange(0, n * 3);
        s.tint.needsUpdate = true;
      }
    }
  }

  /** How much the camera height enlarges a placement (a footing keeps its size; a monument grows less than a prop). */
  private growOf(p: Placement, grow: number): number {
    if (!p.grows) {
      return 1;
    }
    return p.group === 'wonder' ? 1 + (grow - 1) * MODEL_GROW_SHARE : grow;
  }

  /** The slab under a site's monument, or null where none stands: water, a peak, a city, a natural site. */
  private canStand(w: WonderSite, cityHexes: ReadonlySet<number>): Footing | null {
    const t = w.hex;
    const landmark = landmarkOf(w);
    if (!landmark || !this.slots.has(`landmark.${landmark}`) || cityHexes.has(t)) {
      return null;
    }
    if (this.shapes.kind[t] !== HEX_KIND.LAND || this.shapes.peakH[t] > 0) {
      return null;
    }
    return footingAt(this.shapes, t, 0, 0, FOOT_RADIUS);
  }

  /** The footing slab and the landmark on it, at the hex centre. */
  private monument(w: WonderSite, foot: Footing): Placement[] {
    const t = w.hex;
    const landmark = landmarkOf(w);
    const s = landmark ? this.slots.get(`landmark.${landmark}`) : undefined;
    if (!landmark || !s) {
      return [];
    }

    const c = hexCenter(hexCol(t), hexRow(t));
    const yaw = hexHash(t, MONUMENT_SEED) * Math.PI * 2;
    const depth = foot.top - foot.bottom;
    const base = { key: w.key, sourced: w.sourced, group: 'wonder', x: c.x, z: c.z, yaw, grows: false, tint: null } as const;
    return [
      {
        ...base,
        slot: 'footing',
        y: foot.top,
        scale: FOOT_RADIUS,
        depth,
        reach: Math.hypot(FOOT_RADIUS, depth / 2),
        lift: -depth / 2,
        tint: groundTint(this.shapes, t),
      },
      {
        ...base,
        slot: `landmark.${landmark}`,
        y: foot.top,
        scale: 1,
        depth: 1,
        grows: true,
        reach: s.radius,
        lift: s.lift,
      },
    ];
  }

  private resourceProps(t: number, sites: readonly ResourceSite[], company: Company): Placement[] {
    const sea = this.shapes.kind[t] === HEX_KIND.WATER || this.shapes.kind[t] === HEX_KIND.DEEP;
    if (!sea && this.shapes.kind[t] !== HEX_KIND.LAND) {
      return [];
    }

    // At sea oil and gas share one platform; it is sourced only if every record it stands for is.
    const wanted: Array<{ site: ResourceSite; kind: PropKind; sourced: boolean }> = [];
    for (const site of sites) {
      const kind = propKindOf(site.kind, sea);
      if (!kind) {
        continue;
      }

      const same = sea ? wanted.find((w) => w.kind === kind) : undefined;
      if (same) {
        same.sourced = same.sourced && site.sourced;
        continue;
      }
      wanted.push({ site, kind, sourced: site.sourced });
    }
    if (wanted.length === 0) {
      return [];
    }

    // Props stand on a ring round the hex centre; a platform's base is the waterline.
    let radius = wanted.length > 1 ? RING_SHARED : RING_LONE;
    if (this.shapes.peakH[t] > 0) {
      radius = RING_PEAK;
    }
    if (company === 'wonder') {
      radius = RING_WITH_WONDER;
    }
    const spots = ringSpots(wanted.length, hexHash(t, MONUMENT_SEED + 1), sea ? 0 : radius);
    const c = hexCenter(hexCol(t), hexRow(t));
    const out: Placement[] = [];
    wanted.forEach(({ site, kind, sourced }, i) => {
      const s = this.slots.get(kind);
      if (!s) {
        return;
      }

      const spot = spots[i];
      const y = sea ? 0 : groundAt(this.shapes, t, spot.x, spot.z);
      const ore = kind === 'mine' ? heapColor(site.kind) : null;
      out.push({
        key: site.key,
        sourced,
        slot: kind,
        group: 'resource',
        x: c.x + spot.x,
        y,
        z: c.z + spot.z,
        yaw: spot.yaw,
        scale: PROP_SCALE[kind],
        depth: PROP_SCALE[kind],
        grows: true,
        reach: s.radius * PROP_SCALE[kind],
        lift: s.lift * PROP_SCALE[kind],
        tint: ore,
      });
    });
    return out;
  }

  private build(assets: ReadonlyMap<string, THREE.BufferGeometry>): void {
    const get = (id: string): THREE.BufferGeometry | undefined => assets.get(id);
    const castle = get(ASSET.castle);
    const dome = get(ASSET.dome);
    const church = get(ASSET.church);
    const stone = get(ASSET.stone);

    // Monuments: a ground-tinted slab, and the landmarks that stand on it.
    this.addSlot('footing', footingGeometry());
    this.addSlot('landmark.temple', compose([{ geometry: templeGeometry(), scale: TEMPLE_SCALE }]));
    if (castle) {
      this.addSlot('landmark.castle', compose([{ geometry: castle, scale: CASTLE_SCALE }]));
    }
    if (dome) {
      this.addSlot('landmark.dome', compose([{ geometry: dome, scale: DOME_SCALE }]));
    }
    if (church && stone) {
      this.addSlot('landmark.church', compose([
        { geometry: church, x: -CHURCH_OFFSET, scale: CHURCH_SCALE },
        ...stones(stone, STONE_PHASE),
      ]));
    }

    // Resource props, without the dark slab some models carry and with a quieter colour.
    for (const kind of ['pumpjack', 'derrick', 'offshore'] as const) {
      const g = get(ASSET[kind]);
      if (g) {
        this.addSlot(kind, compose([{ geometry: muted(withoutPad(g), PROP_COLOUR_KEEP) }]));
      }
    }
    const headframe = get(ASSET.mine);
    if (headframe) {
      this.addSlot('mine', compose([
        { geometry: muted(headframe, PROP_COLOUR_KEEP), x: HEADFRAME_OFFSET },
        { geometry: heapGeometry(), x: HEAP_OFFSET, z: 0.02 },
      ]));
    }
  }

  private addSlot(id: SlotId, geometry: THREE.BufferGeometry): void {
    geometry.computeBoundingSphere();
    const bound = geometry.boundingSphere ?? new THREE.Sphere();
    let tint: THREE.InstancedBufferAttribute | null = null;
    if (id === 'mine' || id === 'footing') {
      tint = new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3);
      tint.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('iTint', tint);
    }

    const mesh = new THREE.InstancedMesh(geometry, this.material(id), CAPACITY);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.count = 0;
    mesh.visible = false;
    mesh.frustumCulled = false;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = `sites:${id}`;
    this.group.add(mesh);
    this.slots.set(id, { mesh, tint, radius: bound.radius, lift: bound.center.y });
  }

  private material(id: SlotId): PaintedMaterial {
    const m = new PaintedMaterial(this.style, { roughness: ROUGHNESS, metalness: 0 });
    const vertexColor = attribute('color', 'vec3') as V3;
    const mask = attribute('_team', 'float') as unknown as F;
    const glow = attribute('_glow', 'float') as unknown as F;
    const gold = new THREE.Color(GOLD);
    const goldGlow = new THREE.Color(GOLD_GLOW);

    if (id.startsWith('landmark.')) {
      m.colorNode = screenNode(vertexColor.mul(mix(vec3(1), vec3(gold.r, gold.g, gold.b), mask)));
      m.emissiveNode = vec3(goldGlow.r, goldGlow.g, goldGlow.b).mul(glow).mul(GLOW_STRENGTH);
      return m;
    }
    if (id === 'footing') {
      m.colorNode = screenNode(vertexColor.mul(attribute('iTint', 'vec3') as V3));
      return m;
    }
    if (id === 'mine') {
      const ore = attribute('iTint', 'vec3') as V3;
      m.colorNode = screenNode(vertexColor.mul(mix(vec3(1), ore, mask)));
      return m;
    }

    m.colorNode = screenNode(vertexColor);
    if (id === 'offshore') {
      m.emissiveNode = vec3(goldGlow.r, goldGlow.g, goldGlow.b).mul(glow).mul(GLOW_STRENGTH);
    }
    return m;
  }
}

/** Standing stones round a monument's landmark; `phase` turns the ring. */
function stones(geometry: THREE.BufferGeometry, phase: number): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < STONE_COUNT; i++) {
    const a = ((i + phase * 0.5) / STONE_COUNT) * Math.PI * 2 + 2.2;
    parts.push({ geometry, x: Math.cos(a) * STONE_RING, z: Math.sin(a) * STONE_RING, yaw: a, scale: STONE_SCALE });
  }
  return parts;
}
